// Matrix splash screen — shown on initial load.
// Fetches live prices for major indexes + crypto via Alpaca if keys are set.

import { getWorkerBase } from './worker';

const ALPACA_DATA = 'https://data.alpaca.markets';
const CHARS       = '0123456789ABCDEF$%#@!HAWKPLIONQKRMNCRSPGENB01';

interface PriceItem { label: string; price: number | null; change: number | null; }

// ── Price fetchers ────────────────────────────────────────────────────────────

// Yahoo Finance symbols for actual index values (not ETF proxies)
const INDEXES = [
  { yahoo: '%5EGSPC', label: 'S&P 500' },  // ^GSPC
  { yahoo: '%5EIXIC', label: 'NASDAQ'  },  // ^IXIC
  { yahoo: '%5EDJI',  label: 'DOW'     },  // ^DJI
  { yahoo: 'CL%3DF',  label: 'WTI OIL' }, // CL=F crude futures
];

async function fetchIndexes(workerBase: string): Promise<PriceItem[]> {
  try {
    const res  = await fetch(`${workerBase}/api/indexes`, { signal: AbortSignal.timeout(10000) });
    if (!res.ok) throw new Error('');
    const data = await res.json() as { indexes: PriceItem[] };
    return data.indexes ?? [];
  } catch {
    return INDEXES.map(i => ({ label: i.label, price: null, change: null }));
  }
}

async function fetchCrypto(key: string, secret: string): Promise<PriceItem[]> {
  const pairs  = ['BTC/USD', 'ETH/USD', 'SOL/USD', 'XRP/USD', 'DOGE/USD'];
  const labels = ['BTC',     'ETH',     'SOL',     'XRP',     'DOGE'];
  try {
    const res = await fetch(
      `${ALPACA_DATA}/v1beta3/crypto/us/snapshots?symbols=${pairs.map(encodeURIComponent).join(',')}`,
      { headers: { 'APCA-API-KEY-ID': key, 'APCA-API-SECRET-KEY': secret }, signal: AbortSignal.timeout(6000) }
    );
    if (!res.ok) throw new Error('');
    const data = await res.json() as { snapshots?: Record<string, any> };
    const snaps = data.snapshots ?? (data as Record<string, any>);
    return pairs.map((pair, i) => {
      const snap   = snaps[pair];
      const price  = snap?.latestTrade?.p ?? snap?.minuteBar?.c ?? null;
      const prev   = snap?.prevDailyBar?.c ?? null;
      const change = price && prev ? (price - prev) / prev * 100 : null;
      return { label: labels[i], price, change };
    });
  } catch {
    return pairs.map((_, i) => ({ label: labels[i], price: null, change: null }));
  }
}

// ── Formatting ─────────────────────────────────────────────────────────────────

function fmtPrice(item: PriceItem): string {
  if (!item.price) return '—';
  if (item.price >= 1000) return `$${item.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  if (item.price >= 100)  return `$${item.price.toFixed(2)}`;
  return `$${item.price.toFixed(item.price < 1 ? 4 : 2)}`;
}

function fmtChange(item: PriceItem): string {
  if (item.change == null) return '';
  return (item.change >= 0 ? '▲' : '▼') + ' ' + Math.abs(item.change).toFixed(2) + '%';
}

function changeColor(item: PriceItem): string {
  if (item.change == null) return '#3d6478';
  return item.change >= 0 ? '#2ed573' : '#ff4757';
}

// ── Matrix rain ───────────────────────────────────────────────────────────────

function startRain(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')!;
  let cols: number, drops: number[];

  const resize = () => {
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    cols  = Math.floor(canvas.width / 16);
    drops = Array(cols).fill(1);
  };
  resize();
  window.addEventListener('resize', resize);

  const id = setInterval(() => {
    ctx.fillStyle = 'rgba(5,10,14,0.05)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = '13px Space Mono, monospace';
    drops.forEach((y, i) => {
      const char = CHARS[Math.floor(Math.random() * CHARS.length)];
      ctx.fillStyle = Math.random() > 0.95 ? '#ffffff' : i % 3 === 0 ? '#00d4ff' : '#003d5c';
      ctx.fillText(char, i * 16, y * 16);
      if (y * 16 > canvas.height && Math.random() > 0.975) drops[i] = 0;
      drops[i]++;
    });
  }, 50);

  return () => { clearInterval(id); window.removeEventListener('resize', resize); };
}

// ── Render prices ─────────────────────────────────────────────────────────────

function renderPrices(container: HTMLElement, indexes: PriceItem[], crypto: PriceItem[]): void {
  const row = (item: PriceItem) => `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid rgba(0,212,255,0.08);">
      <span style="color:#7a9db5;font-size:12px;letter-spacing:0.1em;min-width:52px;">${item.label}</span>
      <span style="color:#e8f4f8;font-size:13px;font-weight:700;min-width:90px;text-align:right;">${fmtPrice(item)}</span>
      <span style="color:${changeColor(item)};font-size:11px;min-width:72px;text-align:right;">${fmtChange(item)}</span>
    </div>`;

  container.innerHTML = `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;max-width:600px;margin:0 auto;">
      <div>
        <div style="font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#00d4ff;margin-bottom:10px;padding-bottom:5px;border-bottom:1px solid rgba(0,212,255,0.2);">📈 INDEXES</div>
        ${indexes.map(row).join('')}
      </div>
      <div>
        <div style="font-size:9px;letter-spacing:0.2em;text-transform:uppercase;color:#00d4ff;margin-bottom:10px;padding-bottom:5px;border-bottom:1px solid rgba(0,212,255,0.2);">₿ CRYPTO</div>
        ${crypto.map(row).join('')}
      </div>
    </div>`;
}

// ── Main export ───────────────────────────────────────────────────────────────

export function showSplash(): Promise<void> {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.id = 'splash-overlay';
    overlay.style.cssText = `
      position:fixed;inset:0;background:#050a0e;z-index:99999;
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      overflow:hidden;cursor:pointer;
    `;

    // Matrix rain canvas
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;opacity:0.18;';
    overlay.appendChild(canvas);

    // Content
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;z-index:1;text-align:center;padding:20px;width:100%;max-width:680px;';
    content.innerHTML = `
      <div style="font-family:'Space Mono',monospace;font-size:clamp(28px,5vw,48px);font-weight:700;color:#00d4ff;letter-spacing:0.25em;margin-bottom:6px;text-shadow:0 0 30px rgba(0,212,255,0.5);">MERIDIAN</div>
      <div style="font-family:'Space Mono',monospace;font-size:11px;color:#3d6478;letter-spacing:0.3em;margin-bottom:36px;">RESEARCH TERMINAL  v6.0</div>
      <div id="splash-prices" style="margin-bottom:36px;font-family:'Space Mono',monospace;">
        <div style="color:#3d6478;font-size:11px;letter-spacing:0.15em;">FETCHING MARKET DATA…</div>
      </div>
      <button id="splash-enter" style="background:transparent;border:1px solid rgba(0,212,255,0.4);color:#00d4ff;font-family:'Space Mono',monospace;font-size:12px;font-weight:700;padding:12px 40px;cursor:pointer;border-radius:2px;letter-spacing:0.25em;transition:all 0.2s;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto;" onmouseover="this.style.background='rgba(0,212,255,0.08)';this.style.borderColor='#00d4ff'" onmouseout="this.style.background='transparent';this.style.borderColor='rgba(0,212,255,0.4)'">ENTER TERMINAL</button>
      <div style="font-family:'Space Mono',monospace;font-size:9px;color:#1e3448;letter-spacing:0.2em;">PRESS ANY KEY TO CONTINUE</div>
    `;
    overlay.appendChild(content);
    document.body.appendChild(overlay);

    const stopRain = startRain(canvas);

    // Dismiss logic
    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      stopRain();
      overlay.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
      overlay.style.opacity = '0';
      overlay.style.transform = 'scale(1.02)';
      setTimeout(() => { overlay.remove(); resolve(); }, 420);
    };

    document.getElementById('splash-enter')!.addEventListener('click', e => { e.stopPropagation(); dismiss(); });
    document.addEventListener('keydown', dismiss, { once: true });
    overlay.addEventListener('click', dismiss);
    setTimeout(dismiss, 15000); // auto-dismiss after 15s

    // Fetch prices
    const key        = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const secret     = localStorage.getItem('meridian_alpaca_secret') ?? '';
    const workerBase = getWorkerBase();
    const pricesEl   = document.getElementById('splash-prices')!;

    const hasWorker = !!workerBase;
    const hasAlpaca = !!(key && secret);

    if (hasWorker || hasAlpaca) {
      // Indexes via worker proxy (actual values: S&P ~5300, Dow ~42000, etc.)
      const indexPromise = hasWorker
        ? fetchIndexes(workerBase)
        : Promise.resolve(INDEXES.map(i => ({ label: i.label, price: null, change: null })));
      // Crypto via Alpaca
      const cryptoPromise = hasAlpaca
        ? fetchCrypto(key, secret)
        : Promise.resolve(['BTC','ETH','SOL','XRP','DOGE'].map(l => ({ label: l, price: null, change: null })));

      Promise.all([indexPromise, cryptoPromise])
        .then(([indexes, crypto]) => renderPrices(pricesEl, indexes, crypto))
        .catch(() => { pricesEl.innerHTML = '<div style="color:#3d6478;font-size:10px;letter-spacing:0.1em;">Market data unavailable</div>'; });
    } else {
      pricesEl.innerHTML = '<div style="color:#1e3448;font-size:10px;letter-spacing:0.15em;">Configure keys in Settings (Ctrl+Shift+,) to show live prices</div>';
    }
  });
}
